import React, {
  forwardRef,
  ForwardRefRenderFunction,
  Fragment,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Grid, IconButton, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AiOutlinePlus } from "react-icons/ai";
import { FiCheck } from "react-icons/fi";
import { format } from "date-fns-tz";
import * as yup from "yup";

import { AutoSuggest, Input, Select } from "Components/Form";
import { Color } from "Components/Form/Button";
import { SplitButton } from "Components/Form/SplitButton";

import { Expense } from "Models/expenses";
import { Fleet } from "Models/fleet";
import { Member } from "Models/members/member";
import {
  FormHeader,
  FormHeaderTitle,
  FormHeaderRight,
  CostBoxTitle,
  CostBoxPrice,
  BoxForm,
} from "Components/TimeControlModal/styles";
import { formatStringToDate, formatTimeByString } from "Components/TimeControlModal/util";
import { useAppState } from "Context/StateContext";
import {
  CostBox,
  StyledButton,
  TooltipDiv,
  TooltipWrapper,
  WrapperButtons,
  WrapperSelect,
} from "./style";
import useYupValidation from "Hooks/useYupValidation";
import { formatSimpleDate } from "Util/formatDateAndTime";
import { getEquipmentName } from "Util/getEquipmentName";
import IconCloseCircle from "Components/Generic/Icons/CloseCircle";
import { getMemberDescription } from "Util/getMemberDescription";

type TotalExpense = {
  total?: number;
  totalPerOwn?: number;
  totalPerSubcontractor?: number;
};

interface ExpenseManagementHeaderProps {
  expense: Expense | null;
  totalExpense?: TotalExpense;
  fleets: Fleet[];
  members: Member[];
  isLoading?: boolean;
  taskStart?: string | Date;
  taskEnd?: string | Date;

  requirement: string;
  setRequirement: (req: string) => void;

  setExpense: (expense: Expense | null) => void;
  onSubmit: () => void;
  isUserEnableToEdit?: boolean;
}

export interface ExpenseManagementHeaderHandler {
  clearAfterSubmit: () => void;
  setAddNewOption: (option: string) => void;
}

const ExpenseManagementHeaderComp: ForwardRefRenderFunction<
  ExpenseManagementHeaderHandler,
  ExpenseManagementHeaderProps
> = (props, ref) => {
  const {
    expense,
    totalExpense,
    fleets = [],
    members = [],
    requirement,
    setRequirement,
    setExpense,
    onSubmit,
    isLoading = false,
    taskStart,
    taskEnd,
    isUserEnableToEdit = true,
  } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const [addNewOption, setAddNewOption] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const currency = currentUser?.company?.currency;
  const { getErrorByField, fieldHasError } = useYupValidation(errors);

  const returnDateValue = useMemo(() => {
    if (!expense?.date && taskStart) {
      setExpense({ ...(expense || {}), date: new Date(taskStart)?.toISOString() } as Expense);
      return taskStart;
    }

    return formatSimpleDate(expense?.date!, { formatString: "yyyy-MM-dd", isUTC: true });
  }, [expense?.date, taskStart]);

  const returnEditDateValue = (value: string) => {
    const oldDate = new Date(expense?.date ?? new Date());

    const time = format(oldDate, "HH:mm");

    const date = new Date(`${value}T${time}:00`);

    const startDateIso = date.toISOString();

    setExpense({ ...(expense || {}), date: startDateIso } as Expense);
  };

  const onValidate = () => {
    const schema = yup.object().shape({
      type: yup.string().required(),
      description: yup.string().when("type", {
        is: "others",
        then: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        otherwise: yup.string().nullable(),
      }),
      memberId: yup.number().when("type", {
        is: "members",
        then: yup
          .number()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        otherwise: yup.number().nullable(),
      }),
      fleetId: yup.number().when("type", {
        is: "equipments",
        then: yup
          .number()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        otherwise: yup.number().nullable(),
      }),
      date: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      ratePerHour: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      to: yup.string().when("type", {
        is: (value: string) => value !== "others",
        then: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        otherwise: yup.string().nullable(),
      }),
      from: yup.string().when("type", {
        is: (value: string) => value !== "others",
        then: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        otherwise: yup.string().nullable(),
      }),
    });

    try {
      schema.validateSync(
        {
          ...expense,
          type: addNewOption,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }
      return false;
    }
  };

  const isButtonEnabled = useMemo(() => {
    if (addNewOption === "others")
      return !!expense?.description && !!expense?.date && !!expense?.ratePerHour;

    return (
      !!expense?.date &&
      !!expense?.ratePerHour &&
      !!expense?.to &&
      !!expense?.from &&
      (!!expense?.memberId || !!expense?.fleetId)
    );
  }, [addNewOption, expense]);

  const renderAddButton = () => (
    <StyledButton
      customColor={expense?.id ? Color.SUCCESS : Color.BRAND}
      startIcon={expense?.id ? <FiCheck /> : <AiOutlinePlus color="#fff" />}
      loading={isLoading}
      disabled={!isButtonEnabled || isLoading}
      onClick={() => {
        setIsSubmitted(true);

        if (!onValidate()) return;
        onSubmit();
      }}
    >
      {expense?.id ? translate("update") : translate("add")}
    </StyledButton>
  );

  const renderRemoveButton = () => (
    <IconButton
      onClick={() => {
        clearAfterSubmit();
        setExpense(null);
      }}
    >
      <IconCloseCircle height={24} width={24} color="#F04438" />
    </IconButton>
  );

  const regexToPrice = useMemo(() => {
    return /^\d+(\.(\d{1,2})?)?$/;
  }, [currency]);

  const clearAfterSubmit = () => {
    setAddNewOption("");
    setIsSubmitted(false);
    setErrors([]);
  };

  useImperativeHandle(ref, () => ({
    clearAfterSubmit,
    setAddNewOption,
  }));

  useEffect(() => {
    if (expense && isSubmitted) {
      onValidate();
    }
  }, [expense, isSubmitted]);

  return (
    <Grid container direction="row">
      <FormHeader>
        <FormHeaderTitle>{translate("expanseManagement")}</FormHeaderTitle>
        <FormHeaderRight>
          <TooltipWrapper>
            <TooltipDiv className="tooltip-div">
              <div>
                <div>
                  <span>{translate("own")}</span>
                  <span>
                    {currency?.symbol}
                    {(totalExpense?.totalPerOwn || 0).toFixed(Number(currency?.decimalPoints))}
                  </span>
                </div>
                <div>
                  <span>{translate("subcontractor")}</span>
                  <span>
                    {currency?.symbol}
                    {(totalExpense?.totalPerSubcontractor || 0).toFixed(
                      Number(currency?.decimalPoints)
                    )}
                  </span>
                </div>
              </div>
            </TooltipDiv>
            <CostBox>
              <CostBoxTitle>{translate("totalCost")}:</CostBoxTitle>
              <CostBoxPrice>
                {currency?.symbol}
                {(totalExpense?.total || 0).toFixed(Number(currency?.decimalPoints))}
              </CostBoxPrice>
            </CostBox>
          </TooltipWrapper>
          <WrapperSelect>
            <Select
              value={requirement}
              onChange={(e: SelectChangeEvent<any>) => setRequirement(e.target.value)}
            >
              <MenuItem value="">{translate("showAll")}</MenuItem>
              <MenuItem value="fleets">{translate("equipments")}</MenuItem>
              <MenuItem value="members">{translate("members")}</MenuItem>
              <MenuItem value="others">{translate("others")}</MenuItem>
            </Select>
          </WrapperSelect>
          {isUserEnableToEdit ? (
            <SplitButton
              customColor={Color.BRAND}
              options={
                <Fragment>
                  <MenuItem
                    onClick={() => {
                      setExpense(null);
                      setAddNewOption("equipments");
                    }}
                  >
                    {translate("equipments")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setExpense(null);
                      setAddNewOption("members");
                    }}
                  >
                    {translate("members")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setExpense(null);
                      setAddNewOption("others");
                    }}
                  >
                    {translate("others")}
                  </MenuItem>
                </Fragment>
              }
            >
              {translate("addNew")}
            </SplitButton>
          ) : null}
        </FormHeaderRight>
      </FormHeader>

      {addNewOption.trim().length ? (
        <BoxForm>
          <Grid
            container
            direction="row"
            spacing={1}
            alignItems={errors?.length ? "center" : "flex-end"}
          >
            <Grid
              item
              xs={12}
              md={addNewOption === "others" ? 6 : 2}
              lg={addNewOption === "others" ? 6 : 2}
            >
              {addNewOption === "members" ? (
                <AutoSuggest
                  label={translate("addMember")}
                  getOptionLabel={(option: Member) => getMemberDescription(option)}
                  getOptionSelected={(option: Member, value: Member) => option?.id === value?.id}
                  value={expense?.memberId}
                  inputValue={getMemberDescription(expense?.member, { useEmail: false }) || ""}
                  onChange={(member: Member) => {
                    if (member) {
                      setExpense({
                        ...(expense || {}),
                        memberId: member.id,
                        member: member,
                        ratePerHour: member.ratePerHour,
                      } as Expense);
                    } else {
                      setExpense({
                        ...(expense || {}),
                        memberId: undefined,
                        member: undefined,
                        ratePerHour: 0,
                      } as Expense);
                    }
                  }}
                  required
                  options={members}
                  error={fieldHasError("memberId")}
                  errorMessage={getErrorByField("memberId")}
                />
              ) : addNewOption === "equipments" ? (
                <AutoSuggest
                  label={translate("addEquipment")}
                  getOptionLabel={(option: Fleet) => {
                    if (option) {
                      const { fullText } = getEquipmentName(
                        option,
                        translate("general"),
                        translate("fleet")
                      );

                      return fullText;
                    }

                    return "";
                  }}
                  getOptionSelected={(option: Fleet, value: Fleet) => option?.id === value?.id}
                  value={expense?.fleetId}
                  inputValue={
                    expense?.fleet
                      ? getEquipmentName(expense?.fleet, translate("general"), translate("fleet"))
                          ?.fullText
                      : ""
                  }
                  onChange={(fleet: Fleet) => {
                    if (fleet) {
                      setExpense({
                        ...(expense || {}),
                        fleetId: fleet.id,
                        fleet: fleet,
                        ratePerHour: fleet.ratePerHour,
                      } as Expense);
                    } else {
                      setExpense({
                        ...(expense || {}),
                        fleetId: undefined,
                        fleet: undefined,
                        ratePerHour: 0,
                      } as Expense);
                    }
                  }}
                  required
                  options={fleets}
                  error={fieldHasError("fleetId")}
                  errorMessage={getErrorByField("fleetId")}
                />
              ) : (
                <Input
                  label={translate("description")}
                  required
                  placeholder="Enter description"
                  value={expense?.description}
                  onChange={(event) =>
                    setExpense({ ...(expense || {}), description: event.target.value } as Expense)
                  }
                  error={fieldHasError("description")}
                  errorMessage={getErrorByField("description")}
                />
              )}
            </Grid>
            <Grid item xs={12} md={2} lg={2}>
              <Input
                label={translate("date")}
                required
                type="date"
                min={taskStart}
                max={taskEnd ?? taskStart}
                value={returnDateValue}
                onChange={(event) => returnEditDateValue(event.target.value)}
                error={fieldHasError("date")}
                errorMessage={getErrorByField("date")}
              />
            </Grid>
            <Grid item xs={12} md={2} lg={2}>
              <Input
                label={addNewOption === "others" ? translate("amount") : translate("rate")}
                required
                placeholder="0.00"
                value={expense?.ratePerHour || 0}
                leftIcon={<span>{currency?.symbol}</span>}
                rightIcon={addNewOption === "others" ? null : <span>/{translate("hour")}</span>}
                onChange={(event) => {
                  const value = event.target.value;
                  if (!value || (value && regexToPrice.test(value))) {
                    setExpense({
                      ...(expense || {}),
                      ratePerHour: Number(event.target.value),
                    } as Expense);
                  }
                }}
                error={fieldHasError("ratePerHour")}
                errorMessage={getErrorByField("ratePerHour")}
              />
            </Grid>
            {addNewOption !== "others" && (
              <>
                <Grid item xs={12} md={2} lg={2}>
                  <Input
                    label={translate("from")}
                    required
                    type="time"
                    value={formatTimeByString(expense?.from || "")}
                    onChange={(event) =>
                      setExpense({ ...(expense || {}), from: formatStringToDate(event) } as Expense)
                    }
                    error={fieldHasError("from")}
                    errorMessage={getErrorByField("from")}
                  />
                </Grid>
                <Grid item xs={12} md={2} lg={2}>
                  <Input
                    label={translate("to")}
                    required
                    type="time"
                    value={formatTimeByString(expense?.to || "")}
                    onChange={(event) =>
                      setExpense({ ...(expense || {}), to: formatStringToDate(event) } as Expense)
                    }
                    error={fieldHasError("to")}
                    errorMessage={getErrorByField("to")}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={2} lg={2}>
              <WrapperButtons>
                {renderAddButton()}
                {renderRemoveButton()}
              </WrapperButtons>
            </Grid>
          </Grid>
        </BoxForm>
      ) : null}
    </Grid>
  );
};

export const ExpenseManagementHeader = forwardRef(ExpenseManagementHeaderComp);
